import * as React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';
import { QueryStatus } from '@tanstack/react-query';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import { IJob } from 'types/job';
import { IJobsResponse } from 'types/api';
import Box from '@mui/material/Box';
import { styled } from '@mui/material';
import { Counter, TextV2 } from '@insights-ltd/design-library/components';
import TableView from './TableView';

type JobsTableProps = {
  status: QueryStatus;
  data: IJobsResponse | undefined;
  totalJobs: number;
  handleSort: (property: keyof IJob) => void;
  order: 'asc' | 'desc';
  orderBy: keyof IJob;
};

const StyledDiv = styled('div')(({ theme }) => ({
  marginTop: `${theme.spacing(4)} !important`,
  textAlign: 'center',
}));

const JobsTable = ({
  status,
  data,
  totalJobs,
  handleSort,
  order,
  orderBy,
}: JobsTableProps) => {
  const navigate = useNavigate();

  if (status === 'pending') {
    return (
      <StyledDiv>
        <CircularProgress />
        <Typography>Loading...</Typography>
      </StyledDiv>
    );
  }
  if (status === 'error') {
    return (
      <StyledDiv>
        <Typography variant="h6" component="h1">
          Error
        </Typography>
        <Typography>
          There was an error fetching jobs, please try refresh the page.
        </Typography>
      </StyledDiv>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const jobs = data!;

  const navigateToJob = (job: IJob) => navigate(`/jobs/${job.id}`);

  return (
    <>
      <Grid
        container
        spacing={8}
        sx={(theme) => ({
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: theme.spacing(3),
        })}
      >
        <Grid size={8}>
          <TextV2 variant="h3">
            Total Jobs
            <Counter
              count={totalJobs}
              size="large"
              sx={{
                marginLeft: 1,
              }}
              counterAriaLabel="Label entries count"
            />
          </TextV2>
        </Grid>
        <Grid
          size={{
            xs: 2,
            sm: 'auto',
          }}
        >
          <Box>
            <Button
              component={RouterLink}
              to="/jobs/createBulkJob"
              variant="contained"
              color="primary"
              fullWidth
              sx={(theme) => ({
                padding: theme.spacing(1),
              })}
            >
              Start Bulk Job
            </Button>
          </Box>
        </Grid>
        <Grid
          size={{
            xs: 2,
            sm: 'auto',
          }}
        >
          <Button
            component={RouterLink}
            to="/jobs/create"
            variant="contained"
            color="primary"
            fullWidth
            sx={(theme) => ({
              padding: theme.spacing(1),
            })}
          >
            Start New Job
          </Button>
        </Grid>
      </Grid>
      <TableView
        jobs={jobs}
        order={order}
        orderBy={orderBy}
        handleSort={handleSort}
        navigateToJob={navigateToJob}
      />
    </>
  );
};

export default JobsTable;
